import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { TrendChart } from "@/components/dashboards/TrendChart";
import { DrillDownTable, type Column } from "@/components/dashboards/DrillDownTable";
import { DashboardFilters, defaultFilters, type DashboardFilterState } from "@/components/dashboards/DashboardFilters";
import { useTrend, useIncidentsDrillDown } from "@/lib/dashboards";

export const Route = createFileRoute("/_authenticated/dashboards/incidents")({
  component: IncidentsAnalytics,
  head: () => ({ meta: [{ title: "Incident analytics — MissionReady" }] }),
});

type Row = {
  id: string; reference_number: string; title: string | null; subtype: string | null;
  severity: string; status: string; occurrence_at: string;
};

function IncidentsAnalytics() {
  const [filters, setFilters] = useState<DashboardFilterState>(defaultFilters());
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const trend = useTrend("incidents", filters);
  const drill = useIncidentsDrillDown({ ...filters, page, pageSize: 50, search });

  const columns: Column<Row>[] = [
    { key: "reference_number", label: "Ref" },
    { key: "title", label: "Title", render: (r) => r.title || r.subtype || "—" },
    { key: "severity", label: "Severity" },
    { key: "status", label: "Status" },
    { key: "occurrence_at", label: "Occurred", render: (r) => new Date(r.occurrence_at).toLocaleString() },
  ];

  return (
    <div className="space-y-4">
      <DashboardFilters value={filters} onChange={setFilters} />
      <TrendChart title="Incidents over time" points={trend.data?.points} loading={trend.isLoading} color="hsl(var(--warning))" />
      <DrillDownTable
        title="Incidents"
        columns={columns}
        rows={(drill.data?.rows ?? []) as Row[]}
        total={drill.data?.total ?? 0}
        page={page}
        pageSize={50}
        onPageChange={setPage}
        search={search}
        onSearchChange={setSearch}
        loading={drill.isLoading}
        csvName="incidents.csv"
      />
    </div>
  );
}
